const cleanText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const unique = (values = []) => Array.from(new Set(values.map(cleanText).filter(Boolean)));

const getListItemText = (item) => cleanText(
  typeof item === "object" && item
    ? item.label || item.name || item.title || item.value
    : item
);

const formatDate = (value, options = {}) => {
  if (!value) return "";
  const date = value?.toDate?.() || new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...options,
  });
};

const getEntryText = (entry = {}) => {
  const headline = cleanText(entry.headline);
  const explanation = cleanText(entry.explanation);
  if (!headline) return explanation || cleanText(entry.text);
  if (
    explanation
    && explanation.length <= 180
    && explanation.toLowerCase() !== headline.toLowerCase()
  ) {
    return `${headline} — ${explanation}`;
  }
  return headline;
};

const getChapterLines = (chapter) => {
  if (!chapter) return [];
  const handbookLines = (chapter.handbook?.groups || [])
    .flatMap((group) => group.entries || [])
    .map(getEntryText);
  const briefingLines = (chapter.briefing?.items || []).map((item) => cleanText(item.text));
  return unique(handbookLines.length ? handbookLines : briefingLines);
};

const getChapterSourceRecords = (chapter) => (
  (chapter?.handbook?.groups || [])
    .flatMap((group) => group.entries || [])
    .flatMap((entry) => entry.provenance?.records || [])
);

const getSourceTitle = (source = {}) => cleanText(
  source.title
  || source.sourceTitle
  || source.name
  || source.label
  || source.fileName
);

const getSourceType = (source = {}, fallback = "") => cleanText(
  source.type
  || source.sourceType
  || source.sourceTypeLabel
  || source.documentType
  || source.category
  || fallback
);

const getSourceDate = (source = {}) => (
  source.date
  || source.sourceDate
  || source.uploadedAt
  || source.createdAt
  || source.capturedAt
);

const getApprovedDescription = (source = {}) => {
  const value = cleanText(source.shortDescription || source.description);
  if (!value) return "";
  return value.length <= 180 ? value : `${value.slice(0, 177).trim()}...`;
};

const formatSourceReference = (source = {}, fallbackType = "") => {
  const title = getSourceTitle(source);
  if (!title) return "";
  const metadata = [
    getSourceType(source, fallbackType),
    formatDate(getSourceDate(source)),
  ].filter(Boolean).join(" · ");
  const description = getApprovedDescription(source);
  return [
    title,
    metadata ? `(${metadata})` : "",
    description ? `— ${description}` : "",
  ].filter(Boolean).join(" ");
};

const formatMedication = (medication = {}) => {
  const name = cleanText(medication.name);
  if (!name || medication.isArchived) return "";
  const schedules = Array.isArray(medication.schedules) ? medication.schedules : [];
  const scheduleText = schedules
    .map((schedule) => {
      const dose = [schedule?.dose, schedule?.unit].map(cleanText).filter(Boolean).join(" ");
      const timing = cleanText(schedule?.time || schedule?.timing);
      return [dose, timing].filter(Boolean).join(" · ");
    })
    .filter(Boolean)
    .slice(0, 3);
  const fallbackDose = [medication.dose, medication.unit].map(cleanText).filter(Boolean).join(" ");
  const details = scheduleText.length
    ? scheduleText.join("; ")
    : [fallbackDose, cleanText(medication.frequency)].filter(Boolean).join(" · ");
  return details ? `${name} — ${details}` : name;
};

const formatProvider = (provider = {}) => {
  if (provider.isActive === false) return "";
  const name = cleanText(provider.name);
  if (!name) return "";
  const details = [
    cleanText(provider.specialty || provider.role),
    cleanText(provider.organization || provider.practiceName),
    cleanText(provider.phone || provider.phoneNumber),
  ].filter(Boolean).join(" · ");
  return details ? `${name} — ${details}` : name;
};

const createSection = ({ id, title, lines, defaultSelected = true }) => {
  const normalizedLines = unique(lines);
  return {
    id,
    title,
    lines: normalizedLines,
    hasContent: normalizedLines.length > 0,
    defaultSelected,
  };
};

export const buildProfileShareSections = ({
  personName = "This person",
  age = "",
  preferredLanguage = "",
  selectedConditions = [],
  chapters = [],
  medications = [],
  providers = [],
  foodAllergies = [],
  dietaryRestrictions = [],
  documents = {},
  canViewDocuments = false,
} = {}) => {
  const aboutLines = [
    age ? `${personName} is ${age} years old.` : "",
    preferredLanguage ? `Preferred language: ${preferredLanguage}.` : "",
    selectedConditions.length
      ? `Care focus: ${selectedConditions.map((item) => cleanText(item?.label || item)).filter(Boolean).join(", ")}.`
      : "",
  ];
  const chapterSections = chapters.map(({ id, title, chapter }) => createSection({
    id,
    title: cleanText(chapter?.title || title),
    lines: getChapterLines(chapter),
  }));
  const medicationSection = createSection({
    id: "medications",
    title: "Medications",
    lines: medications.map(formatMedication),
    defaultSelected: false,
  });
  const allergySection = createSection({
    id: "allergies_food",
    title: "Allergies & food",
    lines: [
      ...unique(foodAllergies.map(getListItemText)).map((item) => `Allergy: ${item}`),
      ...unique(dietaryRestrictions.map(getListItemText)).map((item) => `Dietary restriction: ${item}`),
    ],
    defaultSelected: false,
  });
  const providerSection = createSection({
    id: "care_providers",
    title: "Care providers",
    lines: providers.map(formatProvider),
    defaultSelected: false,
  });

  const documentReferences = canViewDocuments
    ? Object.entries(documents || {}).flatMap(([category, items]) => (
        Array.isArray(items)
          ? items.map((item) => formatSourceReference(item, category))
          : []
      ))
    : [];
  const chapterSourceReferences = canViewDocuments
    ? chapters.flatMap(({ chapter }) => getChapterSourceRecords(chapter).map((record) => (
        formatSourceReference(record)
      )))
    : [];
  const sourceSection = createSection({
    id: "documents_sources",
    title: "Documents & Sources",
    lines: [...documentReferences, ...chapterSourceReferences],
    defaultSelected: false,
  });

  return [
    createSection({ id: "about", title: "About the person", lines: aboutLines }),
    ...chapterSections,
    medicationSection,
    allergySection,
    providerSection,
    sourceSection,
  ].filter((section) => section.hasContent);
};

export const buildProfileShareSummary = ({
  personName = "This person",
  sections = [],
  selections = {},
  generatedAt = new Date(),
} = {}) => {
  const selectedSections = sections.filter((section) => (
    section.hasContent && selections[section.id] !== false
  ));
  const generatedDate = formatDate(generatedAt, { month: "long" });
  const text = [
    `${personName}'s Care Summary`,
    `Static snapshot generated ${generatedDate || "today"}.`,
    ...selectedSections.flatMap((section) => [
      "",
      section.title,
      ...section.lines.map((line) => `• ${line}`),
    ]),
    "",
    "This is a one-time snapshot. Check the current Profile for the latest information.",
  ].join("\n").replace(/\n{3,}/g, "\n\n").trim();

  return { text, selectedSections };
};
